<script setup lang="ts">
import { ClovyrLogo } from '@clovyr/bed';

const { hideLogo, truncate } = defineProps<{
  hideLogo?: boolean;
  truncate?: boolean; // if true, hides overflowing text with ellipsis
  titleOnly?: boolean; // if true, only shows the title of each feature
}>();

const features = [
  {
    title: 'Automatic Updates and Backups',
    description:
      'Rest easy knowing your applications are always up to date and securely backed up, without the hassle of manual maintenance.',
  },
  {
    title: 'One-Click App Deployment',
    description:
      'Easily launch open-source applications directly into your cloud account or Clovyr Hosting with a single click, simplifying the setup process.',
  },
  {
    title: 'Fully Private & Secure Hosting',
    description:
      'Maintain full control over your data with applications running on your infrastructure; Clovyr never accesses your app data.',
  },
  {
    title: 'Transparent Pricing',
    description:
      'Benefit from flat, monthly subscription plans with no hidden fees; Clovyr charges only for the services provided, not for open-source software.',
  },
  {
    title: 'User-Friendly Interface',
    description:
      'Manage and access your applications through a clean, browser-based UI, making self-hosting accessible to users of all technical levels.',
  },
];

const expanded = ref(false);

const visibleFeatures = computed(() => {
  if (!truncate) return features; // if truncate prop not passed, show all
  return expanded.value ? features : features.slice(0, 3);
});
</script>

<template>
  <div>
    <ClovyrLogo v-if="!hideLogo" class="logo" />

    <ul class="features-list">
      <li
        v-for="(feature, index) in visibleFeatures"
        :key="index"
        class="feature-item"
      >
        <ImageMedia filename="checkmark-icon.svg" class="checkmark" />
        <div class="feature-text">
          <strong>{{ feature.title }}</strong><br />
          <div v-if="!titleOnly">{{ feature.description }}</div>
        </div>
      </li>
    </ul>

    <!-- Toggle only shown if truncate is true -->
    <button
      v-if="truncate && features.length > 2"
      class="toggle-button"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Show Less' : '… Show More' }}
    </button>
  </div>
</template>

<style scoped>
.logo {
  height: 4rem;
  margin-bottom: 2rem;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}

.checkmark {
  width: 24px;
  height: 24px;
  margin-right: 12px;
  flex-shrink: 0;
  margin-top: 4px;
}

.feature-text {
  flex: 1;
}

.toggle-button {
  background: none;
  border: none;
  color: #29bd7b;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
}
</style>
